import React from 'react';
import { View, StyleSheet, Text } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { IconButton } from 'react-native-paper';
import StylusTextInput from './StylusTextInput';
import { colors, spacing, typography } from '../constants/theme';

interface DimensionValue {
  length?: number;
  width?: number;
  depth?: number;
}

interface DimensionInputProps {
  label: string;
  value: DimensionValue;
  onValueChange: (value: DimensionValue) => void;
  unit: 'in' | 'ft' | 'cm' | 'm';
  is3D?: boolean;
  min?: number;
  max?: number;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}

const DimensionInput: React.FC<DimensionInputProps> = ({
  label,
  value,
  onValueChange,
  unit,
  is3D = false,
  min = 0,
  max,
  disabled = false,
  style,
}) => {
  const unitLabels = {
    'in': 'in',
    'ft': 'ft',
    'cm': 'cm',
    'm': 'm'
  };

  const handleDimensionChange = (dimension: 'length' | 'width' | 'depth', newValue: number) => {
    let clampedValue = newValue;
    if (min !== undefined && clampedValue < min) clampedValue = min;
    if (max !== undefined && clampedValue > max) clampedValue = max;

    onValueChange({
      ...value,
      [dimension]: clampedValue
    });
  };

  const handleTextChange = (dimension: 'length' | 'width' | 'depth', text: string) => {
    const parsed = parseInt(text, 10);
    if (isNaN(parsed) || text === '') {
      handleDimensionChange(dimension, min);
    } else {
      handleDimensionChange(dimension, parsed);
    }
  };

  const handleIncrement = (dimension: 'length' | 'width' | 'depth') => {
    if (disabled) return;
    const current = value[dimension] || 0;
    const newVal = current + 1;
    if (max === undefined || newVal <= max) {
      handleDimensionChange(dimension, newVal);
    }
  };

  const handleDecrement = (dimension: 'length' | 'width' | 'depth') => {
    if (disabled) return;
    const current = value[dimension] || 0;
    const newVal = current - 1;
    if (newVal >= min) {
      handleDimensionChange(dimension, newVal);
    }
  };

  const renderDimensionRow = (dimension: 'length' | 'width' | 'depth', dimensionLabel: string) => {
    const dimValue = value[dimension] || 0;
    const isAtMin = dimValue <= min;
    const isAtMax = max !== undefined && dimValue >= max;

    return (
      <View key={dimension} style={styles.dimensionRow}>
        <Text style={styles.dimensionLabel}>{dimensionLabel}</Text>
        <View style={styles.inputGroup}>
          <IconButton
            icon="minus"
            size={20}
            onPress={() => handleDecrement(dimension)}
            disabled={disabled || isAtMin}
            style={styles.button}
          />
          <StylusTextInput
            value={dimValue.toString()}
            onChangeText={(text) => handleTextChange(dimension, text)}
            keyboardType="numeric"
            disabled={disabled}
            style={styles.input}
          />
          <Text style={styles.unitLabel}>{unitLabels[unit]}</Text>
          <IconButton
            icon="plus"
            size={20}
            onPress={() => handleIncrement(dimension)}
            disabled={disabled || isAtMax}
            style={styles.button}
          />
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.dimensionsContainer}>
        {renderDimensionRow('length', 'L')}
        {renderDimensionRow('width', 'W')}
        {is3D && renderDimensionRow('depth', 'D')}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  dimensionsContainer: {
    gap: spacing.sm,
  },
  dimensionRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dimensionLabel: {
    ...typography.body2,
    fontWeight: '600',
    color: colors.textSecondary,
    width: 24,
    marginRight: spacing.xs,
  },
  inputGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  input: {
    width: 80,
    textAlign: 'center',
  },
  unitLabel: {
    ...typography.body2,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
    marginRight: spacing.xs,
    minWidth: 24,
  },
  button: {
    margin: 0,
  },
});

export default DimensionInput;
